import {
  httpsCallable as firebaseHttpsCallable,
  httpsCallableFromURL,
} from "firebase/functions";
import {
  firebaseProjectId,
  functionsRegion,
  usingFunctionsEmulator,
} from "../app/firebase.config";

const emulatorHost = "127.0.0.1";
const emulatorPort = 5001;
const emulatorBaseUrl = `http://${emulatorHost}:${emulatorPort}`;

let emulatorReachabilityState = usingFunctionsEmulator ? "unknown" : "disabled";
let emulatorProbePromise = null;
let emulatorFallbackLogged = false;

const isCallableUrl = (value) => /^https?:\/\//i.test(String(value || "").trim());

const buildProductionCallableUrl = (name) => {
  if (!firebaseProjectId || !name) {
    return "";
  }

  return `https://${functionsRegion}-${firebaseProjectId}.cloudfunctions.net/${name}`;
};

const probeFunctionsEmulator = async () => {
  if (!usingFunctionsEmulator) {
    return false;
  }

  if (emulatorReachabilityState === "reachable") {
    return true;
  }

  if (emulatorReachabilityState === "unreachable") {
    return false;
  }

  if (!emulatorProbePromise) {
    emulatorProbePromise = fetch(emulatorBaseUrl, {
      method: "GET",
      mode: "no-cors",
      cache: "no-store",
    })
      .then(() => {
        emulatorReachabilityState = "reachable";
        return true;
      })
      .catch(() => {
        emulatorReachabilityState = "unreachable";
        return false;
      })
      .finally(() => {
        emulatorProbePromise = null;
      });
  }

  return emulatorProbePromise;
};

const logEmulatorFallbackOnce = (name) => {
  if (emulatorFallbackLogged) {
    return;
  }

  emulatorFallbackLogged = true;
  console.warn(
    `Firebase Functions emulator at ${emulatorHost}:${emulatorPort} is unavailable. Falling back to deployed callable${name ? ` "${name}"` : ""}.`
  );
};

export const httpsCallable = (functionsInstance, name, options) => {
  if (isCallableUrl(name)) {
    return httpsCallableFromURL(functionsInstance, name, options);
  }

  const invokeLocalCallable = firebaseHttpsCallable(functionsInstance, name, options);
  const productionUrl = buildProductionCallableUrl(name);
  const invokeProductionCallable =
    productionUrl && usingFunctionsEmulator
      ? httpsCallableFromURL(functionsInstance, productionUrl, options)
      : null;

  return async (data) => {
    if (!usingFunctionsEmulator || !invokeProductionCallable) {
      return invokeLocalCallable(data);
    }

    if (emulatorReachabilityState === "unreachable") {
      return invokeProductionCallable(data);
    }

    try {
      const result = await invokeLocalCallable(data);
      emulatorReachabilityState = "reachable";
      return result;
    } catch (error) {
      const emulatorAvailable = await probeFunctionsEmulator();
      if (!emulatorAvailable) {
        logEmulatorFallbackOnce(name);
        return invokeProductionCallable(data);
      }

      throw error;
    }
  };
};
